import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { paramsPatch } from '@/lib/urlParams'
import { PALLET_STATUS_LABEL } from '@/lib/palletStatus'

export const PALLETS_SCOPE_KEY = 'kf49.pallets.scope' // gitleaks:allow (a storage key, not a secret)
export const PALLETS_ALL_STATUS_KEY = 'kf49.pallets.allStatusFilter' // gitleaks:allow (a storage key, not a secret)

/** Home's "pallets rejected" alert opens the Pallets register on that status
 *  (F09 UF-15): All pallets, read from the server, filtered to what it counts. */
export const PALLETS_REJECTED_PATH = '/pallets?status=INSPECTED_REJECTED'

/** `?status=` checked against the pallet status list. A value that is not a
 *  status (junk, a typo, an old bookmark) is no link: the plain register. */
export function readPalletsStatusLink(params: URLSearchParams): string | null {
  const s = params.get('status')
  return s && Object.prototype.hasOwnProperty.call(PALLET_STATUS_LABEL, s) ? s : null
}

/**
 * A link into Pallets with `?status=`: opens All pallets on that status chip
 * for THIS visit only. The device's saved By run / All pallets scope and chip
 * are never written, so the next plain visit opens as it did before the tap.
 * The link is read in the state initialiser, so the first render (and the
 * first fetch) is already the link's; an effect then drops it from the
 * address. `nonce` changes per link so a mounted view re-applies a second one.
 */
export function usePalletsStatusLink() {
  const [params, setParams] = useSearchParams()
  const [link, setLink] = useState<{ status: string; nonce: number } | null>(() => {
    const status = readPalletsStatusLink(params)
    return status ? { status, nonce: 1 } : null
  })
  // The mount's link is already in state; only a later one is new.
  const mounted = useRef(false)
  useEffect(() => {
    const status = readPalletsStatusLink(params)
    const isLater = mounted.current
    mounted.current = true
    if (!status) return
    if (isLater) setLink(prev => ({ status, nonce: (prev?.nonce ?? 0) + 1 }))
    setParams(prev => paramsPatch(prev, { status: null }), { replace: true })
  }, [params]) // eslint-disable-line react-hooks/exhaustive-deps
  return link
}
